import {lazy} from 'react';

/**
 * A wrapper for React.lazy which retries the import for a limited number of times on chunk load error
 * Based on https://github.com/floydjones1/react-lazy-retry/blob/main/src/index.ts
 * @param {function} component A function supported by React.lazy (expected to be a dynamic import)
 * @return {function} React.lazy function
 */

const MAX_RETRIES = 5;
const RETRY_INTERVAL = 1000;

export default function lazyRetry(component) {
  return lazy(() => retry(component));
}

/**
 * Execute a Promise-returning function after a set delay, with an iteration countdown
 * Based on https://github.com/floydjones1/react-lazy-retry/blob/main/src/index.ts
 * @param {function} fn A Promise-returning function
 * @param {number} retries The maximum number of times to retry
 * @param {number} interval The base amount of time to wait between retries
 * @return {Promise}
 */
export function retry(fn, retries = MAX_RETRIES, interval = RETRY_INTERVAL) {
  return new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch(error => {
        setTimeout(() => {
          if (retries === 1) {
            reject(error);
          } else {
            retry(fn, retries - 1, interval).then(resolve, reject);
          }
        }, interval);
      });
  });
}
